import { captureErrorToSentry } from "@helpers/sentry-logger";
import type { OriginalItemTypes } from "@memorang/types";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import useSummativeAssessmentStore from "../hooks/useSummativeAssessmentStore";
import { fetchFeedbackResult } from "./feedback-client";
import { computeClientGrading } from "./feedback-client-grading";
import { postPrebuiltAnswerEvent } from "./post-ai-answer-event";
import {
	hasCapturedFeedbackResponse,
	resolveFeedbackUserResponse,
	serializeFeedbackUserResponse,
	toFeedbackAiResponse,
} from "./pte-feedback-response";
import { findSessionItemById } from "./session";
import { defaultSleep } from "./sleep";
import type { RunAssessmentCtx } from "./summative-assessment-runner";
import { withRetries } from "./with-retries";

export type RunFeedbackCtx = RunAssessmentCtx;

const MAX_CONCURRENT_FEEDBACK_RUNS = 3;

let activeFeedbackRuns = 0;
const feedbackRunQueue: (() => void)[] = [];

const acquireFeedbackSlot = (): Promise<void> => {
	if (activeFeedbackRuns < MAX_CONCURRENT_FEEDBACK_RUNS) {
		activeFeedbackRuns += 1;
		return Promise.resolve();
	}
	return new Promise((resolve) => {
		feedbackRunQueue.push(() => {
			activeFeedbackRuns += 1;
			resolve();
		});
	});
};

const releaseFeedbackSlot = () => {
	activeFeedbackRuns -= 1;
	feedbackRunQueue.shift()?.();
};

export const runFeedback = async (
	itemId: number,
	ctx: RunFeedbackCtx,
): Promise<void> => {
	const sessionState = useSessionStore.getState();
	const item = findSessionItemById(sessionState.sessionItems, itemId);
	const itemTypeId = item?.metadata?.type as OriginalItemTypes | undefined;
	if (!(item && itemTypeId)) {
		return;
	}
	if (!hasCapturedFeedbackResponse(item)) {
		return;
	}
	const resolvedResponse = resolveFeedbackUserResponse(item);
	const userResponse = serializeFeedbackUserResponse(resolvedResponse);
	const clientGrading = computeClientGrading(item, resolvedResponse);

	// Allocate seq before queueing for a concurrency slot, so it reflects
	// invocation order, not how long this run waited behind other runs.
	const mySeq = useSummativeAssessmentStore
		.getState()
		.beginRun(itemId, userResponse);

	const sleep = ctx.sleep ?? defaultSleep;

	await acquireFeedbackSlot();
	try {
		if (!useSummativeAssessmentStore.getState().isLatestRun(itemId, mySeq)) {
			return;
		}

		const result = await withRetries(
			() =>
				fetchFeedbackResult({
					item,
					userResponse,
					...(clientGrading ? { clientGrading } : {}),
					userId: ctx.userId,
					projectId: ctx.projectId,
					organizationId: ctx.organizationId,
					sessionId: sessionState.id,
				}),
			sleep,
		);

		if (!useSummativeAssessmentStore.getState().isLatestRun(itemId, mySeq)) {
			return;
		}

		if (!result) {
			captureErrorToSentry(
				new Error("Summative feedback: feedback agent call exhausted retries"),
				{ itemId },
			);
			useSummativeAssessmentStore.getState().markError(itemId, mySeq);
			return;
		}

		const aiFeedbackStore = useAIFeedbackStore.getState();
		aiFeedbackStore.updateExplanation(itemId, result.feedback);
		aiFeedbackStore.updateAnswer(itemId, userResponse);
		aiFeedbackStore.updateStatus(itemId, "COMPLETE");

		const stashedRequest = useSummativeAssessmentStore
			.getState()
			.getStashedFeedbackRequest(itemId);
		if (!stashedRequest) {
			captureErrorToSentry(
				new Error(
					"Summative feedback: no stashed answer-event request to follow up on",
				),
				{ itemId },
			);
			// Re-fireable on a future revisit: this run graded successfully, it just
			// has nothing safe to persist yet (e.g. a resume re-fire before the item
			// was ever posted this session).
			useSummativeAssessmentStore.getState().markFeedbackStranded(itemId);
			useSummativeAssessmentStore.getState().markError(itemId, mySeq);
			return;
		}

		const posted = await postPrebuiltAnswerEvent(
			{
				...stashedRequest,
				aiResponse: toFeedbackAiResponse({
					feedback: result.feedback,
					answer: userResponse,
					status: "COMPLETE",
				}),
			},
			sleep,
		);

		if (posted) {
			useSummativeAssessmentStore.getState().clearFeedbackStranded(itemId);
			useSummativeAssessmentStore.getState().markAssessed(itemId, mySeq);
		} else {
			useSummativeAssessmentStore.getState().markError(itemId, mySeq);
		}
	} finally {
		releaseFeedbackSlot();
	}
};
